import { fullySelectedSegments } from "./lasso.ts";
import type { LngLat } from "./types.ts";
import type { SelectAllStep } from "./selection.ts";

export type LinePattern = "solid" | "dashed" | "dotted";

export interface PointStyle {
  color: string;
  /** Circle radius in pixels. */
  size: number;
}

export interface LineStyle {
  color: string;
  /** Stroke width in pixels. */
  width: number;
  pattern: LinePattern;
}

export interface StyleDefaults {
  point: PointStyle;
  line: LineStyle;
}

export const POINT_SIZE_MIN = 2;
export const POINT_SIZE_MAX = 32;
export const LINE_WIDTH_MIN = 1;
export const LINE_WIDTH_MAX = 24;

/** Resting vertex and the plain (no road match) stroke. New features copy these. */
export function defaultStyleDefaults(): StyleDefaults {
  return {
    point: { color: "#ffffff", size: 5.5 },
    line: { color: "#6b7280", width: 4.5, pattern: "solid" },
  };
}

export function clonePointStyle(style: PointStyle): PointStyle {
  return { color: style.color, size: style.size };
}

export function cloneLineStyle(style: LineStyle): LineStyle {
  return { color: style.color, width: style.width, pattern: style.pattern };
}

export function samePointStyle(a: PointStyle | null, b: PointStyle | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.color === b.color && a.size === b.size;
}

export function sameLineStyle(a: LineStyle | null, b: LineStyle | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.color === b.color && a.width === b.width && a.pattern === b.pattern;
}

export function samePointStyleList(a: readonly (PointStyle | null)[], b: readonly (PointStyle | null)[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (!samePointStyle(a[i] ?? null, b[i] ?? null)) return false;
  }
  return true;
}

export function sameLineStyleList(a: readonly (LineStyle | null)[], b: readonly (LineStyle | null)[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (!sameLineStyle(a[i] ?? null, b[i] ?? null)) return false;
  }
  return true;
}

export function lineStyleKey(style: LineStyle | null): string {
  if (!style) return "";
  return `${style.color}|${style.width}|${style.pattern}`;
}

const PATTERNS = new Set<LinePattern>(["solid", "dashed", "dotted"]);

export function normalizePointStyle(value: unknown): PointStyle | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as { color?: unknown; size?: unknown };
  const color = normalizeHex(raw.color);
  const size = clampNumber(raw.size, POINT_SIZE_MIN, POINT_SIZE_MAX);
  if (!color || size == null) return null;
  return { color, size };
}

export function normalizeLineStyle(value: unknown): LineStyle | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as { color?: unknown; width?: unknown; pattern?: unknown };
  const color = normalizeHex(raw.color);
  const width = clampNumber(raw.width, LINE_WIDTH_MIN, LINE_WIDTH_MAX);
  if (!color || width == null || !isPattern(raw.pattern)) return null;
  return { color, width, pattern: raw.pattern };
}

export function parseStyleDefaults(value: unknown): StyleDefaults {
  const base = defaultStyleDefaults();
  if (!value || typeof value !== "object") return base;
  const raw = value as { point?: unknown; line?: unknown };
  return {
    point: normalizePointStyle(raw.point) ?? base.point,
    line: normalizeLineStyle(raw.line) ?? base.line,
  };
}

/** Align to `count`. Missing entries stay unstyled. A non-array is all unstyled. */
export function alignPointStyles(value: unknown, count: number): Array<PointStyle | null> {
  return alignStyles(value, count, normalizePointStyle);
}

export function alignLineStyles(value: unknown, count: number): Array<LineStyle | null> {
  return alignStyles(value, count, normalizeLineStyle);
}

/** Omit the array when every entry is unstyled so older saves stay small. */
export function packPointStyles(
  styles: readonly (PointStyle | null)[],
  count: number,
): Array<PointStyle | null> | undefined {
  return packStyles(styles, count, (style) => (style ? clonePointStyle(style) : null));
}

export function packLineStyles(
  styles: readonly (LineStyle | null)[],
  count: number,
): Array<LineStyle | null> | undefined {
  return packStyles(styles, count, (style) => (style ? cloneLineStyle(style) : null));
}

export function clonePointStyles(
  styles: readonly (PointStyle | null)[],
  count: number,
): Array<PointStyle | null> {
  const out: Array<PointStyle | null> = new Array(count).fill(null);
  for (let i = 0; i < count; i++) {
    const style = styles[i];
    out[i] = style ? clonePointStyle(style) : null;
  }
  return out;
}

export function cloneLineStyles(styles: readonly (LineStyle | null)[], count: number): Array<LineStyle | null> {
  const out: Array<LineStyle | null> = new Array(count).fill(null);
  for (let i = 0; i < count; i++) {
    const style = styles[i];
    out[i] = style ? cloneLineStyle(style) : null;
  }
  return out;
}

/**
 * Old index → new index, or -1 when that point is gone.
 * Points that share a coordinate keep their style. New coordinates are uncovered.
 */
export function matchPointsForStyles(before: readonly LngLat[], after: readonly LngLat[]): Int32Array {
  const buckets = new Map<string, number[]>();
  for (let i = after.length - 1; i >= 0; i--) {
    const point = after[i];
    if (!point) continue;
    const key = `${point[0]},${point[1]}`;
    const list = buckets.get(key);
    if (list) list.push(i);
    else buckets.set(key, [i]);
  }
  const map = new Int32Array(before.length).fill(-1);
  const used = new Uint8Array(after.length);
  for (let i = 0; i < before.length; i++) {
    const point = before[i];
    if (!point) continue;
    const list = buckets.get(`${point[0]},${point[1]}`);
    if (!list) continue;
    while (list.length > 0) {
      const next = list.pop();
      if (next == null || used[next] === 1) continue;
      used[next] = 1;
      map[i] = next;
      break;
    }
  }
  return map;
}

/**
 * Surviving points keep their style. A point with no old index copies `fallback`
 * (the Settings default). `pointMap` is old → new, or null when nothing survived.
 */
export function remapPointStyles(
  styles: readonly (PointStyle | null)[],
  pointMap: Int32Array | null,
  nextCount: number,
  fallback: PointStyle,
): Array<PointStyle | null> {
  const out: Array<PointStyle | null> = new Array(nextCount).fill(null);
  if (!pointMap) {
    const stamped = clonePointStyle(fallback);
    for (let i = 0; i < nextCount; i++) out[i] = clonePointStyle(stamped);
    return out;
  }
  const covered = new Uint8Array(nextCount);
  for (let old = 0; old < pointMap.length; old++) {
    const next = pointMap[old] ?? -1;
    if (next < 0 || next >= nextCount || covered[next] === 1) continue;
    covered[next] = 1;
    const style = styles[old];
    out[next] = style ? clonePointStyle(style) : null;
  }
  for (let i = 0; i < nextCount; i++) {
    if (covered[i] === 0) out[i] = clonePointStyle(fallback);
  }
  return out;
}

/**
 * An edge whose endpoints both survived keeps its style.
 * A split copies the parent style onto each half.
 * Any other new edge copies `fallback`.
 */
export function remapLineStyles(
  styles: readonly (LineStyle | null)[],
  oldSegments: ReadonlyArray<readonly [number, number]>,
  newSegments: ReadonlyArray<readonly [number, number]>,
  pointMap: Int32Array | null,
  fallback: LineStyle,
): Array<LineStyle | null> {
  const out: Array<LineStyle | null> = new Array(newSegments.length).fill(null);
  if (!pointMap) {
    const stamped = cloneLineStyle(fallback);
    for (let i = 0; i < newSegments.length; i++) out[i] = cloneLineStyle(stamped);
    return out;
  }
  const buckets = new Map<string, Array<LineStyle | null>>();
  for (let i = 0; i < oldSegments.length; i++) {
    const pair = oldSegments[i];
    if (!pair) continue;
    const a = pointMap[pair[0]] ?? -1;
    const b = pointMap[pair[1]] ?? -1;
    if (a < 0 || b < 0 || a === b) continue;
    const key = a < b ? `${a}:${b}` : `${b}:${a}`;
    const current = styles[i] ?? null;
    const style = current ? cloneLineStyle(current) : null;
    const list = buckets.get(key);
    if (list) list.push(style);
    else buckets.set(key, [style]);
  }
  const survived = new Set<number>();
  for (let i = 0; i < pointMap.length; i++) {
    const next = pointMap[i] ?? -1;
    if (next >= 0) survived.add(next);
  }
  const unmatched: number[] = [];
  for (let i = 0; i < newSegments.length; i++) {
    const pair = newSegments[i];
    if (!pair) continue;
    const key = pair[0] < pair[1] ? `${pair[0]}:${pair[1]}` : `${pair[1]}:${pair[0]}`;
    const list = buckets.get(key);
    if (list && list.length > 0) {
      const style = list.pop() ?? null;
      out[i] = style ? cloneLineStyle(style) : null;
      continue;
    }
    unmatched.push(i);
  }
  for (const index of unmatched) {
    const pair = newSegments[index];
    if (!pair) {
      out[index] = cloneLineStyle(fallback);
      continue;
    }
    const aFresh = !survived.has(pair[0]);
    const bFresh = !survived.has(pair[1]);
    let inherited: LineStyle | null | undefined;
    if (aFresh !== bFresh) {
      const kept = aFresh ? pair[1] : pair[0];
      for (const [key, list] of buckets) {
        if (list.length === 0) continue;
        const [x, y] = key.split(":");
        if (Number(x) !== kept && Number(y) !== kept) continue;
        const style = list[list.length - 1] ?? null;
        inherited = style ? cloneLineStyle(style) : null;
        break;
      }
    }
    out[index] = inherited === undefined ? cloneLineStyle(fallback) : inherited;
  }
  return out;
}

function edgeKey(a: number, b: number): string {
  return a < b ? `${a}:${b}` : `${b}:${a}`;
}

/**
 * Styles after J. Existing lines stay put (join appends). Each new segment
 * copies a line already touching an endpoint: the endpoint earlier in `order`
 * first, then the other. A null line does not count. When neither endpoint
 * has a styled line, the new segment copies `fallback` (the piece default).
 * A null fallback keeps the road-band, matching older unsaved pieces.
 */
export function joinedLineStyles(
  oldSegments: ReadonlyArray<readonly [number, number]>,
  oldStyles: readonly (LineStyle | null)[],
  newSegments: ReadonlyArray<readonly [number, number]>,
  order: readonly number[],
  fallback: LineStyle | null = null,
): Array<LineStyle | null> {
  const out: Array<LineStyle | null> = new Array(newSegments.length).fill(null);
  const kept = Math.min(oldSegments.length, newSegments.length);
  for (let i = 0; i < kept; i++) {
    const style = oldStyles[i] ?? null;
    out[i] = style ? cloneLineStyle(style) : null;
  }
  const incident = new Map<number, LineStyle>();
  for (let i = 0; i < oldSegments.length; i++) {
    const style = oldStyles[i];
    if (!style) continue;
    const pair = oldSegments[i];
    if (!pair) continue;
    const cloned = cloneLineStyle(style);
    if (!incident.has(pair[0])) incident.set(pair[0], cloned);
    if (!incident.has(pair[1])) incident.set(pair[1], pair[0] === pair[1] ? cloned : cloneLineStyle(style));
  }
  const seen = new Set<string>();
  for (const pair of oldSegments) {
    if (!pair || pair[0] === pair[1]) continue;
    seen.add(edgeKey(pair[0], pair[1]));
  }
  let cursor = oldSegments.length;
  for (let i = 1; i < order.length && cursor < newSegments.length; i++) {
    const a = order[i - 1];
    const b = order[i];
    if (a == null || b == null || a === b) continue;
    const key = edgeKey(a, b);
    if (seen.has(key)) continue;
    seen.add(key);
    const style = incident.get(a) ?? incident.get(b) ?? fallback;
    out[cursor] = style ? cloneLineStyle(style) : null;
    cursor += 1;
  }
  return out;
}

/**
 * Styles after a dissolve. A segment that survived keeps the first old line
 * on that edge. A bridge copies the removed lines: when they agree, that
 * style; otherwise the earliest line (lowest segment index), even when it is null.
 * A bridge that was skipped because the edge already existed does not appear here.
 */
export function dissolvedLineStyles(
  oldStyles: readonly (LineStyle | null)[],
  oldSegments: ReadonlyArray<readonly [number, number]>,
  newSegments: ReadonlyArray<readonly [number, number]>,
  pointMap: Int32Array | null,
  collapsedFrom: readonly (readonly number[])[],
): Array<LineStyle | null> {
  const out: Array<LineStyle | null> = new Array(newSegments.length).fill(null);
  const survived = new Map<string, LineStyle | null>();
  if (pointMap) {
    for (let i = 0; i < oldSegments.length; i++) {
      const pair = oldSegments[i];
      if (!pair) continue;
      const a = pointMap[pair[0]] ?? -1;
      const b = pointMap[pair[1]] ?? -1;
      if (a < 0 || b < 0 || a === b) continue;
      const key = edgeKey(a, b);
      if (survived.has(key)) continue;
      const style = oldStyles[i] ?? null;
      survived.set(key, style ? cloneLineStyle(style) : null);
    }
  }
  for (let i = 0; i < newSegments.length; i++) {
    const from = collapsedFrom[i] ?? [];
    if (from.length === 0) {
      const pair = newSegments[i];
      if (!pair) continue;
      const style = survived.get(edgeKey(pair[0], pair[1])) ?? null;
      out[i] = style ? cloneLineStyle(style) : null;
      continue;
    }
    let first = Infinity;
    for (const index of from) {
      if (index >= 0 && index < first) first = index;
    }
    const style = first === Infinity ? null : (oldStyles[first] ?? null);
    out[i] = style ? cloneLineStyle(style) : null;
  }
  return out;
}

export interface StyleTargets {
  points: number[];
  lines: number[];
}

/**
 * Points in the selection, plus lines that are clicked or whose both ends are
 * selected. Points-only select-all leaves the lines alone.
 */
export function collectStyleTargets(
  pointCount: number,
  segments: ReadonlyArray<readonly [number, number]>,
  selection: readonly number[],
  selectedLines: readonly number[],
  selectAll: SelectAllStep | null,
): StyleTargets {
  const seen = new Set<number>();
  const points: number[] = [];
  for (const index of selection) {
    if (index < 0 || index >= pointCount || seen.has(index)) continue;
    seen.add(index);
    points.push(index);
  }
  if (selectAll === "points") return { points, lines: [] };
  const lineSeen = new Set<number>();
  const lines: number[] = [];
  const pushLine = (index: number) => {
    if (index < 0 || index >= segments.length || lineSeen.has(index)) return;
    lineSeen.add(index);
    lines.push(index);
  };
  for (const index of selectedLines) pushLine(index);
  if (points.length > 0) {
    for (const index of fullySelectedSegments(segments, points)) pushLine(index);
  }
  return { points, lines };
}

export interface StyleFormValues {
  point: PointStyle;
  line: LineStyle;
  pointMixed: boolean;
  lineMixed: boolean;
}

/** The value to show. Mixed selections still show the first item's style. */
export function styleFormValues(
  pointStyles: readonly (PointStyle | null)[],
  lineStyles: readonly (LineStyle | null)[],
  targets: StyleTargets,
  defaults: StyleDefaults,
): StyleFormValues {
  const point = consensus(targets.points, (index) => pointStyles[index] ?? null, defaults.point, samePointStyle, clonePointStyle);
  const line = consensus(targets.lines, (index) => lineStyles[index] ?? null, defaults.line, sameLineStyle, cloneLineStyle);
  return { point: point.value, line: line.value, pointMixed: point.mixed, lineMixed: line.mixed };
}

/**
 * Write `point` onto selected points and `line` onto selected lines.
 * A null patch leaves that kind unchanged. One call is one undo step.
 */
export interface StyleClipboard {
  point: PointStyle | null;
  line: LineStyle | null;
}

/**
 * Style of the first selected object of each kind.
 * That is the object the Style panel shows. An unstyled object copies the
 * Settings default, which is the color, size, width, and pattern the panel shows.
 * An empty selection copies nothing.
 */
export function copySelectionStyles(
  pointStyles: readonly (PointStyle | null)[],
  lineStyles: readonly (LineStyle | null)[],
  targets: StyleTargets,
  defaults: StyleDefaults,
): StyleClipboard | null {
  if (targets.points.length === 0 && targets.lines.length === 0) return null;
  const pointIndex = targets.points[0];
  const lineIndex = targets.lines[0];
  return {
    point: pointIndex == null ? null : clonePointStyle(pointStyles[pointIndex] ?? defaults.point),
    line: lineIndex == null ? null : cloneLineStyle(lineStyles[lineIndex] ?? defaults.line),
  };
}

/**
 * Write the clipboard onto the matching selection.
 * A point style never touches lines, and a line style never touches points.
 * A missing side, or a selection with nothing of that kind, is left alone.
 * The result is one patch, so the editor can store it as one undo step.
 */
export function pasteSelectionStyles(
  pointStyles: readonly (PointStyle | null)[],
  lineStyles: readonly (LineStyle | null)[],
  pointCount: number,
  lineCount: number,
  targets: StyleTargets,
  clipboard: StyleClipboard,
): { pointStyles: Array<PointStyle | null>; lineStyles: Array<LineStyle | null>; changed: boolean } {
  const point = clipboard.point && targets.points.length > 0 ? clipboard.point : null;
  const line = clipboard.line && targets.lines.length > 0 ? clipboard.line : null;
  if (!point && !line) {
    return {
      pointStyles: clonePointStyles(pointStyles, pointCount),
      lineStyles: cloneLineStyles(lineStyles, lineCount),
      changed: false,
    };
  }
  return applyStylePatch(pointStyles, lineStyles, pointCount, lineCount, targets, point, line);
}

export function applyStylePatch(
  pointStyles: readonly (PointStyle | null)[],
  lineStyles: readonly (LineStyle | null)[],
  pointCount: number,
  lineCount: number,
  targets: StyleTargets,
  point: PointStyle | null,
  line: LineStyle | null,
): { pointStyles: Array<PointStyle | null>; lineStyles: Array<LineStyle | null>; changed: boolean } {
  const nextPoints = clonePointStyles(pointStyles, pointCount);
  const nextLines = cloneLineStyles(lineStyles, lineCount);
  let changed = false;
  if (point && targets.points.length > 0) {
    const stamped = clonePointStyle(point);
    for (const index of targets.points) {
      if (!samePointStyle(nextPoints[index] ?? null, stamped)) changed = true;
      nextPoints[index] = clonePointStyle(stamped);
    }
  }
  if (line && targets.lines.length > 0) {
    const stamped = cloneLineStyle(line);
    for (const index of targets.lines) {
      if (!sameLineStyle(nextLines[index] ?? null, stamped)) changed = true;
      nextLines[index] = cloneLineStyle(stamped);
    }
  }
  return { pointStyles: nextPoints, lineStyles: nextLines, changed };
}

function consensus<T>(
  indexes: readonly number[],
  read: (index: number) => T | null,
  fallback: T,
  same: (a: T | null, b: T | null) => boolean,
  clone: (value: T) => T,
): { value: T; mixed: boolean } {
  let first: T | null | undefined;
  let mixed = false;
  for (const index of indexes) {
    const style = read(index);
    if (first === undefined) first = style;
    else if (!same(first, style)) mixed = true;
  }
  const value = first == null ? fallback : first;
  return { value: clone(value), mixed };
}

function alignStyles<T>(value: unknown, count: number, normalize: (item: unknown) => T | null): Array<T | null> {
  const out: Array<T | null> = new Array(count).fill(null);
  if (!Array.isArray(value)) return out;
  const limit = Math.min(count, value.length);
  for (let i = 0; i < limit; i++) out[i] = normalize(value[i]);
  return out;
}

function packStyles<T>(
  styles: readonly (T | null)[],
  count: number,
  clone: (style: T | null) => T | null,
): Array<T | null> | undefined {
  const out: Array<T | null> = new Array(count).fill(null);
  let any = false;
  for (let i = 0; i < count; i++) {
    const style = clone(styles[i] ?? null);
    out[i] = style;
    if (style) any = true;
  }
  return any ? out : undefined;
}

function normalizeHex(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const hex = value.trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(hex)) return hex;
  if (/^#[0-9a-f]{3}$/.test(hex)) {
    const r = hex[1];
    const g = hex[2];
    const b = hex[3];
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return null;
}

function clampNumber(value: unknown, min: number, max: number): number | null {
  const number = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  if (!Number.isFinite(number)) return null;
  const clamped = Math.min(max, Math.max(min, number));
  return Math.round(clamped * 2) / 2;
}

function isPattern(value: unknown): value is LinePattern {
  return typeof value === "string" && PATTERNS.has(value as LinePattern);
}
